// frontend/src/components/Modals/InvoiceScheduleModal.jsx
// ✅ VERSIÓN SIMPLIFICADA - SIN CAMPOS SAT, CON FACILITADOR

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Badge,
  Card,
  CardBody,
} from 'reactstrap';

import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { parseDate } from '../../utils/dates';

const InvoiceScheduleModal = ({ isOpen, toggle, order, quoteData, onConfirm, isPartialMode = false, prefillData = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [facilitators, setFacilitators] = useState([]);
  const [loadingFacilitators, setLoadingFacilitators] = useState(false);
  const quoteBaseAmount = parseFloat(quoteData?.subtotal ?? quoteData?.total ?? 0) || 0;
  const orderTotalAmount = parseFloat(
    order?.total_amount ?? order?.total ?? order?.subtotal ?? quoteBaseAmount
  ) || 0;
  const scheduledAmountTotal = parseFloat(order?.scheduled_amount_total ?? 0) || 0;
  const remainingToSchedule = parseFloat(
    order?.remaining_to_schedule ?? Math.max(orderTotalAmount - scheduledAmountTotal, 0)
  ) || 0;
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount) || 0);
  
  const [sessions, setSessions] = useState([{ date: '', facilitator_id: '', notes: '' }]);
  const [formData, setFormData] = useState({
    scheduled_invoice_date: '',
    scheduled_amount: '',
    responsible_user_id: user?.id || '',
    location: '',
    client_need: '',
    billing_name: '',
    billing_rfc: '',
    billing_address: '',
    credit_days: 0,
    description: '',
    total_hours: '',
    items: {},
    notes: '',
    entry_requirements: '',
    entry_contact_name: '',
    entry_contact_phone: '',
    workshop_name: '',
    program_required: false,
    session_schedule: '',
    diplomas_required: false,
  });

  // ✅ Cargar facilitadores
  useEffect(() => {
    loadFacilitators();
  }, []);

  const loadFacilitators = async () => {
    try {
      setLoadingFacilitators(true);
      const response = await api.get('/facilitators/', { params: { is_active: true } });
      setFacilitators(response.data.results || response.data);
    } catch (error) {
      console.error('Error cargando facilitadores:', error);
    } finally {
      setLoadingFacilitators(false);
    }
  };

  // ✅ Generar URL del PDF (solo si existe)
  useEffect(() => {
    // ✅ NUEVO: Si es orden manual, no hay PDF
    if (order?.is_manual) {
      setPdfUrl(null);
      console.log('📄 Orden manual detectada - sin PDF del cliente');
      return;
    }
    
    if (order?.pdf_file) {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
      // Obtener la ruta del PDF
      let pdfPath = order.pdf_file;
    
      // Normalizar separadores (Windows usa \, convertir a /)
      pdfPath = pdfPath.replace(/\\/g, '/');

      // Si es URL absoluta, quedarnos con la ruta después de /media/
      const mediaIdx = pdfPath.indexOf('/media/');
      if (/^https?:\/\//i.test(pdfPath) && mediaIdx !== -1) {
        pdfPath = pdfPath.substring(mediaIdx + '/media/'.length);
      }

      // Remover 'media/' del inicio si existe
      pdfPath = pdfPath.replace(/^\/?(media\/)?/, '');
    
      let url;
    
      // Detectar si es archivo temporal o archivado
      if (pdfPath.startsWith('temp/') || pdfPath.includes('/temp/')) {
        // Archivo temporal - usar serve-temp-pdf
        const filename = pdfPath.split('/').pop();
        url = `${baseURL}/api/orders/serve-temp-pdf/?file=${encodeURIComponent(filename)}`;
        console.log('📄 PDF temporal detectado:', filename);
      } else if (pdfPath.startsWith('po_archive/')) {
        // Archivo archivado - remover prefijo y usar serve-archive-pdf
        const archivePath = pdfPath.replace(/^po_archive\//, '');
        url = `${baseURL}/api/orders/serve-archive-pdf/?file=${encodeURIComponent(archivePath)}`;
        console.log('📄 PDF archivado detectado:', archivePath);
      } else {
        // Asumir que es archivo archivado sin prefijo
        url = `${baseURL}/api/orders/serve-archive-pdf/?file=${encodeURIComponent(pdfPath)}`;
        console.log('📄 PDF (asumido archivado):', pdfPath);
      }
    
      setPdfUrl(url);
      console.log('📄 PDF URL generada:', url);
    } else {
      setPdfUrl(null);
    }

    // Cleanup
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [order]);


  // Pre-llenar desde schedule existente (flujo: OC llega después de aceptación manual)
  useEffect(() => {
    if (prefillData && isOpen) {
      if (prefillData.facilitation_sessions?.length) {
        setSessions(prefillData.facilitation_sessions.map(s => ({
          date: s.date || '',
          facilitator_id: s.facilitator_id ? String(s.facilitator_id) : '',
          notes: s.notes || '',
        })));
      } else if (prefillData.service_delivery_date) {
        setSessions([{
          date: prefillData.service_delivery_date,
          facilitator_id: prefillData.facilitator_id ? String(prefillData.facilitator_id) : '',
          notes: '',
        }]);
      }
      setFormData(prev => ({
        ...prev,
        scheduled_invoice_date: prefillData.scheduled_invoice_date || '',
        location: prefillData.location || '',
        client_need: prefillData.client_need || '',
        billing_name: prefillData.billing_name || prev.billing_name,
        billing_rfc: prefillData.billing_rfc || prev.billing_rfc,
        billing_address: prefillData.billing_address || prev.billing_address,
        credit_days: prefillData.credit_days ?? prev.credit_days,
        scheduled_amount: prefillData.scheduled_amount || prev.scheduled_amount,
        description: prefillData.description || prev.description,
        total_hours: prefillData.total_hours || prev.total_hours,
        entry_requirements: prefillData.entry_requirements || '',
        entry_contact_name: prefillData.entry_contact_name || '',
        entry_contact_phone: prefillData.entry_contact_phone || '',
        workshop_name: prefillData.workshop_name || '',
        program_required: prefillData.program_required || false,
        session_schedule: prefillData.session_schedule || '',
        diplomas_required: prefillData.diplomas_required || false,
      }));
    } else if (isOpen) {
      setSessions([{ date: '', facilitator_id: '', notes: '' }]);
    }
  }, [prefillData, isOpen]);

  // ✅ Pre-llenar datos desde propuesta
  useEffect(() => {
    // ✅ NUEVO: Si order es solo datos (no tiene .id), es una orden manual pendiente
    const isManualPending = order && !order.id;
    
    if (quoteData) {
      const items = quoteData.items || {};
      const clientInfo = items.client_info || {};
      const serviceDetails = items.service_details || {};
      const payment = items.payment || {};
    
      const legalName = clientInfo.legal_name || 
                        (isManualPending ? '' : order?.billing_name) || 
                        quoteData.client_name || '';
    
      let totalHours = serviceDetails.num_hours || quoteData.total_hours || 0;
    
      if (!totalHours && items.items && Array.isArray(items.items)) {
        totalHours = items.items.reduce((sum, item) => {
          return sum + (parseFloat(item.hours) || 0);
        }, 0);
      }
    
      const creditDays = payment.credit_days || quoteData.credit_days || 0;
    
      let description = '';
      if (items.items && Array.isArray(items.items)) {
        description = items.items
          .map(item => {
            const desc = item.description || item.name || '';
            const hours = item.hours || serviceDetails.num_hours || '';
            return hours ? `${desc} - ${hours} hrs` : desc;
          })
          .filter(Boolean)
          .join('\n');
      }
    
      if (!description && serviceDetails.service_type) {
        description = `${serviceDetails.service_type}`;
        if (totalHours) {
          description += ` - ${totalHours} horas`;
        }
      }

      setFormData(prev => ({
        ...prev,
        billing_name: legalName,
        billing_rfc: isManualPending ? '' : (order?.billing_rfc || ''),
        billing_address: isManualPending ? '' : (order?.billing_address || ''),
        credit_days: creditDays,
        description: description,
        total_hours: totalHours > 0 ? totalHours.toString() : '',
        items: items || {},
      }));
    }
  }, [order, quoteData]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!isPartialMode) {
      const missingDate = sessions.some(s => !s.date);
      if (missingDate) errors.push('Todas las sesiones deben tener fecha');
      if (!formData.scheduled_invoice_date) errors.push('Fecha de facturación es obligatoria');
      if (!formData.scheduled_amount) errors.push('Monto a facturar es obligatorio');
      if (!formData.billing_name) errors.push('Razón social es obligatoria');
      if (!formData.billing_rfc) errors.push('RFC es obligatorio');
      if (!formData.billing_address) errors.push('Dirección es obligatoria');
      if (!formData.description) errors.push('Descripción es obligatoria');
    }

    if (formData.total_hours && parseFloat(formData.total_hours) <= 0) {
      errors.push('Las horas deben ser mayor a 0');
    }

    if (!isPartialMode) {
      const scheduledAmount = parseFloat(formData.scheduled_amount);
      if (!Number.isNaN(scheduledAmount)) {
        if (scheduledAmount <= 0) {
          errors.push('El monto a facturar debe ser mayor a 0');
        }
        if (scheduledAmount > remainingToSchedule) {
          errors.push('El monto a facturar no puede exceder el saldo pendiente de la orden');
        }
      }
    }
    return errors;
  };

  const handleSubmit = async () => {
    setError(null);

    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }

    setLoading(true);

    try {
      const firstSession = sessions[0];
      const submitData = {
        ...formData,
        // legado: primera sesión como campos raíz para compatibilidad
        service_delivery_date: firstSession?.date || null,
        facilitator_id: firstSession?.facilitator_id ? parseInt(firstSession.facilitator_id) : null,
        // nuevas sesiones múltiples
        facilitation_sessions: sessions
          .filter(s => s.date)
          .map(s => ({
            date: s.date,
            facilitator_id: s.facilitator_id ? parseInt(s.facilitator_id) : null,
            notes: s.notes || '',
          })),
        responsible_user_id: parseInt(formData.responsible_user_id),
        credit_days: parseInt(formData.credit_days) || 0,
        total_hours: formData.total_hours ? parseFloat(formData.total_hours) : null,
        scheduled_amount: formData.scheduled_amount ? parseFloat(formData.scheduled_amount) : undefined,
        is_partial: isPartialMode,
      };

      console.log('📤 Enviando datos de programación:', submitData);

      await onConfirm(submitData);
      toggle();
    } catch (err) {
      console.error('❌ Error al programar facturación:', err);
      
      let errorMessage = 'Error al programar facturación';
      
      // ✅ MEJORADO: Manejo de errores más robusto
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Caso 1: error.field_name (objeto con campos)
        if (typeof errorData === 'object' && errorData.error) {
          const innerError = errorData.error;
          
          if (typeof innerError === 'object') {
            // Extraer todos los errores de campos
            errorMessage = Object.entries(innerError)
              .map(([key, value]) => {
                const fieldName = key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase());
                const errorValue = Array.isArray(value) ? value.join(', ') : value;
                return `${fieldName}: ${errorValue}`;
              })
              .join('; ');
          } else {
            errorMessage = innerError;
          }
        }
        // Caso 2: Errores directos en response.data
        else if (typeof errorData === 'object') {
          errorMessage = Object.entries(errorData)
            .map(([key, value]) => {
              const fieldName = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
              const errorValue = Array.isArray(value) ? value.join(', ') : value;
              return `${fieldName}: ${errorValue}`;
            })
            .join('; ');
        }
        // Caso 3: String directo
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal 
      isOpen={isOpen} 
      size="xl"
      backdrop="static" 
      keyboard={false}
      className="modal-dialog-centered"
      style={{ maxWidth: '95vw' }}
    >
      <ModalHeader 
        toggle={toggle}
        className="bg-gradient-primary border-0"
        style={{
          background: 'linear-gradient(135deg, #038D81 0%, #026B62 100%)',
        }}
      >
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h5 className="mb-0 text-white font-weight-bold">
            <i className="ni ni-calendar-grid-58 me-2"></i>
            Programar Facturación - Orden {order?.order_number}
            {order?.is_manual && (
              <Badge color="warning" className="ms-2 badge-sm">
                Orden Manual
              </Badge>
            )}
          </h5>

          <small className="text-white opacity-8 d-block mt-1">
            {isPartialMode
              ? 'Llena los datos que tengas disponibles — puedes completar el resto cuando llegue la OC'
              : 'Completa los datos de facturación mientras visualizas el PDF de la orden'}
          </small>
        </div>
      </ModalHeader>

      <ModalBody className="p-0" style={{ height: '75vh', overflow: 'hidden' }}>
        <Row className="g-0 h-100">
          {/* ========== COLUMNA IZQUIERDA: FORMULARIO (60%) ========== */}
          <Col md="7" className="border-end" style={{ 
            overflowY: 'auto', 
            height: '100%',
            background: '#f8fafc'
          }}>
            <div className="p-4">
              {isPartialMode && (
                <Alert color="warning" className="shadow-sm mb-3">
                  <span className="alert-icon"><i className="ni ni-bell-55"></i></span>
                  <span className="alert-text">
                    <strong>Modo parcial:</strong> Todos los campos son opcionales. Llena lo que tengas — cuando suba la OC podrás completar el resto.
                  </span>
                </Alert>
              )}

              {error && (
                <Alert color="danger" className="shadow-sm">
                  <span className="alert-icon"><i className="ni ni-support-16"></i></span>
                  <span className="alert-text">{error}</span>
                </Alert>
              )}

              <Alert color="bg-gradient-success" className="shadow-sm">
                <span className="alert-icon"><i className="ni ni-check-bold"></i></span>
                <span className="alert-text">
                  <strong>Datos pre-llenados desde la propuesta:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Razón Social: {formData.billing_name || 'N/A'}</li>
                    <li>Días de crédito: {formData.credit_days} días</li>
                    <li>Horas totales: {formData.total_hours || 'N/A'}</li>
                    <li>Descripción del servicio</li>
                  </ul>
                  <small className="d-block mt-2 opacity-8">Puedes editar cualquier campo si es necesario</small>
                </span>
              </Alert>

              <Alert color="info" className="shadow-sm">
                <span className="alert-icon"><i className="ni ni-chart-pie-35"></i></span>
                <span className="alert-text">
                  <strong>Avance de programación de la orden:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Monto total OC: {formatCurrency(orderTotalAmount)}</li>
                    <li>Programado acumulado: {formatCurrency(scheduledAmountTotal)}</li>
                    <li>Pendiente por programar: {formatCurrency(remainingToSchedule)}</li>
                  </ul>
                </span>
              </Alert>

              <Form>
                {/* SESIONES DE FACILITACIÓN */}
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-calendar-grid-58 me-2"></i>
                      Sesiones de Facilitación
                    </h6>
                    <Button
                      size="sm"
                      color="default"
                      className="btn-icon text-white border-white"
                      style={{ background: 'rgba(255,255,255,0.15)' }}
                      onClick={() => setSessions(prev => [...prev, { date: '', facilitator_id: '', notes: '' }])}
                      type="button"
                    >
                      <i className="ni ni-fat-add me-1"></i> Agregar sesión
                    </Button>
                  </div>
                  <div className="card-body p-3">
                    {sessions.map((session, idx) => (
                      <div
                        key={idx}
                        className="p-3 mb-3 border rounded"
                        style={{ background: '#f8fafc', position: 'relative' }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-xs font-weight-bold text-uppercase text-muted">
                            Sesión {idx + 1}
                          </span>
                          {sessions.length > 1 && (
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              type="button"
                              onClick={() => setSessions(prev => prev.filter((_, i) => i !== idx))}
                              style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                            >
                              <i className="ni ni-fat-remove"></i>
                            </Button>
                          )}
                        </div>
                        <Row>
                          <Col md="6">
                            <FormGroup className="mb-2">
                              <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                                Fecha <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="date"
                                className="form-control-alternative"
                                value={session.date}
                                onChange={e => setSessions(prev => prev.map((s, i) => i === idx ? { ...s, date: e.target.value } : s))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup className="mb-2">
                              <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                                Facilitador
                              </Label>
                              <Input
                                type="select"
                                className="form-control-alternative"
                                value={session.facilitator_id}
                                onChange={e => setSessions(prev => prev.map((s, i) => i === idx ? { ...s, facilitator_id: e.target.value } : s))}
                                disabled={loadingFacilitators}
                              >
                                <option value="">Sin asignar</option>
                                {facilitators.map(f => (
                                  <option key={f.id} value={f.id}>
                                    {f.name}{f.specialty ? ` - ${f.specialty}` : ''}
                                  </option>
                                ))}
                              </Input>
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Notas de la sesión
                          </Label>
                          <Input
                            type="text"
                            className="form-control-alternative"
                            value={session.notes}
                            onChange={e => setSessions(prev => prev.map((s, i) => i === idx ? { ...s, notes: e.target.value } : s))}
                            placeholder="Opcional"
                          />
                        </FormGroup>
                      </div>
                    ))}

                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Fecha Límite para Facturar <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="date"
                            name="scheduled_invoice_date"
                            className="form-control-alternative"
                            value={formData.scheduled_invoice_date}
                            onChange={handleChange}
                            required
                          />
                          <small className="text-muted d-block mt-1">
                            Fecha límite para generar la factura
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Monto a Facturar <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="number"
                            name="scheduled_amount"
                            className="form-control-alternative"
                            value={formData.scheduled_amount}
                            onChange={handleChange}
                            min="0.01"
                            step="0.01"
                            placeholder="Ej: 50000"
                            required
                          />
                          <small className="text-muted d-block mt-1">
                            Máx: {formatCurrency(remainingToSchedule)}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Horario de Sesiones */}
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Horario de las Sesiones
                          </Label>
                          <Input
                            type="text"
                            name="session_schedule"
                            className="form-control-alternative"
                            value={formData.session_schedule}
                            onChange={handleChange}
                            placeholder="Ej: 09:00 - 13:00"
                          />
                          <small className="text-muted d-block mt-1">
                            Especifica el horario de inicio y fin
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ✅ NUEVO: NECESIDAD DEL CLIENTE */}
                    
                  </div>
                </div>

                {/* Datos de Facturación */}
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-single-copy-04 me-2"></i>
                      Datos de Facturación
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Razón Social <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="billing_name"
                            className="form-control-alternative"
                            value={formData.billing_name}
                            onChange={handleChange}
                            placeholder="Nombre completo de la empresa"
                            required
                          />
                          
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            RFC <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="billing_rfc"
                            className="form-control-alternative"
                            value={formData.billing_rfc}
                            onChange={handleChange}
                            placeholder="RFC del cliente"
                            maxLength="13"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Dirección Fiscal <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            name="billing_address"
                            className="form-control-alternative"
                            value={formData.billing_address}
                            onChange={handleChange}
                            placeholder="Dirección completa para facturación"
                            rows="2"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* ✅ TÉRMINOS DE PAGO SIMPLIFICADOS */}
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-money-coins me-2"></i>
                      Términos de Pago
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Días de Crédito
                          </Label>
                          <Input
                            type="number"
                            name="credit_days"
                            className="form-control-alternative"
                            value={formData.credit_days}
                            onChange={handleChange}
                            min="0"
                          />
                          
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* Descripción y Horas */}
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-align-left-2 me-2"></i>
                      Descripción del Servicio
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Descripción  <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            name="description"
                            className="form-control-alternative"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Descripción detallada del servicio facturado"
                            rows="4"
                            required
                          />
                          
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Total de Horas {formData.total_hours && <span className="text-danger">*</span>}
                          </Label>
                          <Input
                            type="number"
                            name="total_hours"
                            className="form-control-alternative"
                            value={formData.total_hours}
                            onChange={handleChange}
                            placeholder="Total de horas del servicio"
                            step="0.01"
                            min="0"
                          />
                          {formData.total_hours ? (
                            <>
                              
                              
                            </>
                          ) : (
                            <small className="text-muted d-block mt-1">
                              Opcional si el servicio no se mide por horas
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>
                                {/* 🆕 NUEVA SECCIÓN: DETALLES DEL TALLER */}
                <div className="card shadow border-0 mb-3">
                  <div className="card-header bg-gradient-primary">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-hat-3 me-2"></i>
                      Detalles del Taller
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    {/* Nombre del Taller */}
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Nombre del Taller
                          </Label>
                          <Input
                            type="text"
                            name="workshop_name"
                            className="form-control-alternative"
                            value={formData.workshop_name}
                            onChange={handleChange}
                            placeholder="Nombre específico del taller"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Col md="12">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            <i className="ni ni-bulb-61 me-1"></i>
                            Necesidad del Cliente
                          </Label>
                          <Input
                            type="textarea"
                            name="client_need"
                            className="form-control-alternative"
                            value={formData.client_need}
                            onChange={handleChange}
                            placeholder="Describe la necesidad/objetivo"
                            rows="3"
                          />
                          
                        </FormGroup>
                      </Col>
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            <i className="ni ni-pin-3 me-1"></i>
                            Lugar  de impartición
                          </Label>
                          <Input
                            type="textarea"
                            name="location"
                            className="form-control-alternative"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Lugar donde se impartió el servicio (opcional)"
                            rows="2"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Requisitos de Ingreso */}
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Requisitos de Ingreso
                          </Label>
                          <Input
                            type="textarea"
                            name="entry_requirements"
                            className="form-control-alternative"
                            value={formData.entry_requirements}
                            onChange={handleChange}
                            placeholder="Ej: Zapato industrial, casco, etc."
                            rows="2"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Contacto de Entrada */}
                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Contacto de Entrada
                          </Label>
                          <Input
                            type="text"
                            name="entry_contact_name"
                            className="form-control-alternative"
                            value={formData.entry_contact_name}
                            onChange={handleChange}
                            placeholder="Nombre de quien da acceso al lugar"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                            Teléfono del Contacto
                          </Label>
                          <Input
                            type="text"
                            name="entry_contact_phone"
                            className="form-control-alternative"
                            value={formData.entry_contact_phone}
                            onChange={handleChange}
                            placeholder="Ej: 444 123 4567"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Programa y Diplomas */}
                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold d-block">
                            ¿Es un Programa?
                          </Label>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="program_required"
                              name="program_required"
                              checked={formData.program_required}
                              onChange={handleChange}
                            />
                            <label className="custom-control-label" htmlFor="program_required">
                              Sí, es un programa
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label className="form-control-label text-uppercase text-xs font-weight-bold d-block">
                            ¿Requiere Diplomas?
                          </Label>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="diplomas_required"
                              name="diplomas_required"
                              checked={formData.diplomas_required}
                              onChange={handleChange}
                            />
                            <label className="custom-control-label" htmlFor="diplomas_required">
                              Sí, requiere diplomas
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>

                

                {/* Notas Adicionales */}
                <FormGroup className="mb-0">
                  <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                    Notas Adicionales
                  </Label>
                  <Input
                    type="textarea"
                    name="notes"
                    className="form-control-alternative"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Notas o comentarios adicionales (opcional)"
                    rows="2"
                  />
                </FormGroup>
              </Form>
            </div>
          </Col>

          {/* ========== COLUMNA DERECHA: PDF VIEWER (40%) ========== */}
          <Col md="5" className="bg-white" style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div 
              className="p-3 border-bottom"
              style={{
                background: 'linear-gradient(to right, #f8fafc, #e8f5f4)',
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 font-weight-bold text-dark">
                    <i className="ni ni-single-copy-04 me-2 text-primary"></i>
                    Vista Previa del PDF
                  </h6>
                  <small className="text-muted">
                    Orden: {order?.order_number}
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    size="sm"
                    className="btn-icon"
                    onClick={() => window.open(pdfUrl, '_blank')}
                    disabled={!pdfUrl}
                    title="Abrir en nueva pestaña"
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-laptop"></i>
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            <div 
              className="flex-grow-1 position-relative"
              style={{ 
                background: '#f1f5f9',
                overflow: 'hidden'
              }}
            >
              {pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=0`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Vista Previa Orden de Compra"
                />
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                  <div 
                    className={`icon icon-shape ${order?.is_manual ? 'bg-gradient-warning' : 'bg-gradient-primary'} shadow-primary text-center rounded-circle mb-3`}
                    style={{ width: '80px', height: '80px' }}
                  >
                    <i className={`ni ${order?.is_manual ? 'ni-single-02' : 'ni-folder-17'} text-white`} style={{ fontSize: '2rem', lineHeight: '80px' }}></i>
                  </div>
                  {order?.is_manual ? (
                    <>
                      <h5 className="text-dark">Orden Manual</h5>
                      <p className="text-sm text-muted text-center px-4">
                        Esta orden fue creada manualmente.<br />
                        El cliente confirmó sin enviar OC formal.
                      </p>
                      {order.confirmation_method && (
                        <div className="bg-warning-light p-3 border-radius-lg mt-3" style={{ backgroundColor: '#fff3cd', maxWidth: '90%' }}>
                          <p className="text-xs mb-1">
                            <strong>Medio:</strong> {
                              order.confirmation_method === 'email' ? 'Correo Electrónico' :
                              order.confirmation_method === 'phone' ? 'Llamada Telefónica' :
                              order.confirmation_method === 'whatsapp' ? 'WhatsApp' :
                              order.confirmation_method === 'verbal' ? 'Verbal/Presencial' :
                              'Otro'
                            }
                          </p>
                          <p className="text-xs mb-0">
                            <strong>Fecha:</strong> {order.confirmation_date ? parseDate(order.confirmation_date).toLocaleDateString('es-MX') : 'N/A'}
                          </p>
                          {order.confirmation_reference && (
                            <p className="text-xs mb-0 mt-2">
                              <strong>Ref:</strong> {order.confirmation_reference}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <h5 className="text-dark">PDF no disponible</h5>
                      <p className="text-sm text-muted">
                        El documento no se pudo cargar
                      </p>
                    </>
                  )}
                </div>
              )}

            </div>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-0 bg-light">
        <Button 
          color="secondary" 
          onClick={toggle} 
          disabled={loading}
        >
          <i className="ni ni-fat-remove me-2"></i>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleSubmit} 
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Programando...
            </>
          ) : (
            <>
              <i className="ni ni-check-bold me-2"></i>
              Programar Facturación
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InvoiceScheduleModal;


